import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  CardMedia,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import { Transition } from "../../Constants/Constant";
import { MdOutlineCancel, MdDelete } from "react-icons/md";
import { FaPlus } from "react-icons/fa";
import AxiosClient from "../../Helpers/axios";
import { IoMdSave } from "react-icons/io";
import { IoCloudUpload } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const AddProduct = ({ fetchProducts }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [thumbnailId, setThumbnailId] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryUrls, setGalleryUrls] = useState([]);

  const [productInfo, setProductInfo] = useState({
    name: "",
    price: "",
    category: "",
    description: "",
  });

  const handleOnchange = (e) => {
    setProductInfo({ ...productInfo, [e.target.name]: e.target.value });
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFileUpload = async (e) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    formData.append("fileType", "products");

    try {
      const { data } = await AxiosClient.post("api/file", formData);
      setThumbnailId(data.fileKey);
      toast.success("Picture uploaded successfully", {
        autoClose: 500,
        theme: "colored",
      });
      setThumbnailUrl(URL.createObjectURL(e.target.files[0]));
    } catch (error) {
      toast.error("Picture upload failed", {
        autoClose: 500,
        theme: "colored",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    formData.append("fileType", "products");

    try {
      const { data } = await AxiosClient.post("api/file", formData);
      const newImage = {
        fileKey: data.fileKey,
        position: galleryImages.length + 1,
      };
      setGalleryImages([...galleryImages, newImage]);
      setGalleryUrls([...galleryUrls, URL.createObjectURL(e.target.files[0])]);
      toast.success("Picture uploaded successfully", {
        autoClose: 500,
        theme: "colored",
      });
    } catch (error) {
      toast.error("Picture upload failed", {
        autoClose: 500,
        theme: "colored",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const newGalleryImages = [...galleryImages];
    const newGalleryUrls = [...galleryUrls];
    newGalleryImages.splice(index, 1);
    newGalleryUrls.splice(index, 1);
    setGalleryImages(newGalleryImages);
    setGalleryUrls(newGalleryUrls);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !productInfo.name ||
      !productInfo.price ||
      !productInfo.category ||
      !thumbnailId
    ) {
      toast.error("Please fill all the fields", {
        autoClose: 500,
        theme: "colored",
      });
      return;
    }

    if (productInfo.price <= 0) {
      toast.error("Please enter positive price amount", { autoClose: 500 });
      return;
    }

    setLoading(true);

    try {
      await AxiosClient.post("api/admin/addproduct", {
        name: productInfo.name,
        price: productInfo.price,
        categoryId: productInfo.category,
        thumbnail: thumbnailId,
        description: productInfo.description,
        images: galleryImages,
      });
      setOpen(false);

      toast.success("Product added successfully", {
        autoClose: 500,
        theme: "colored",
      });
      setProductInfo({
        name: "",
        image: "",
        price: "",
        category: "",
        description: "",
      });
      setThumbnailUrl(null);
      setGalleryImages([]);
      setGalleryUrls([]);
      fetchProducts();
      navigate(`/admin/home?table=product`);
    } catch (error) {
      toast.error("Something went wrong", {
        autoClose: 500,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    const { data } = await AxiosClient.get("api/category");

    setCategories(data.categories);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <>
      <Box
        sx={{
          textAlign: "right",
          margin: "20px 0",
        }}
      >
        <Button
          variant="contained"
          endIcon={<FaPlus />}
          onClick={handleClickOpen}
        >
          Create
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        keepMounted
        TransitionComponent={Transition}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "bold", color: "#1976d2" }}
        >
          Create product
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    name="name"
                    value={productInfo.name}
                    onChange={handleOnchange}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">
                      Category
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={productInfo.category}
                      label="Category"
                      name="category"
                      onChange={handleOnchange}
                    >
                      {categories.map((item) => (
                        <MenuItem value={item.id} key={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {thumbnailUrl ? (
                    <CardMedia
                      component="img"
                      image={thumbnailUrl}
                      alt="Picture"
                      style={{ maxHeight: "300px", margin: "10px 0px" }}
                    />
                  ) : null}
                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Upload main picture"}
                    <IoCloudUpload />
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFileUpload}
                    />
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Upload gallery images"}
                    <IoCloudUpload />
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleGalleryUpload}
                    />
                  </Button>
                  {galleryUrls.length > 0 && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      {galleryUrls.map((url, index) => (
                        <Grid item xs={4} key={index}>
                          <Box position="relative">
                            <CardMedia
                              component="img"
                              image={url}
                              alt={`Gallery Image ${index + 1}`}
                              style={{ maxHeight: "150px" }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                color: "white",
                                backgroundColor: "rgba(0,0,0,0.5)",
                              }}
                              onClick={() => handleRemoveImage(index)}
                            >
                              <MdDelete />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Price"
                    name="price"
                    value={productInfo.price}
                    onChange={handleOnchange}
                    variant="outlined"
                    inputMode="numeric"
                    type="number"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sx={{ margin: "10px auto" }}>
                  <TextField
                    id="filled-textarea"
                    value={productInfo.description}
                    onChange={handleOnchange}
                    label="Description"
                    multiline
                    sx={{ width: "100%" }}
                    variant="outlined"
                    name="description"
                    minRows={5}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <DialogActions
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  type="reset"
                  color="error"
                  onClick={handleClose}
                  endIcon={<MdOutlineCancel />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  endIcon={<IoMdSave />}
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Create"}
                </Button>
              </DialogActions>
            </form>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddProduct;
